import { computed, shallowRef } from "vue";
import { defineStore } from "pinia";
import type { ProjectSettingsSectionId } from "../components/ProjectSettingsDialog.vue";
import { isJust, isNothing, just, matchMaybe, toMaybe } from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { dispatchInto } from "../lib/tea";
import { writeZwConfig } from "../lib/tauri/project";
import type {
  ManuscriptItem,
  ZwConfig,
  ZwDatabaseFieldDef,
  ZwDatabaseRelationPair,
  ZwFonts,
  ZwScene,
} from "../lib/zwConfig";
import { normalizeZwGoals } from "../lib/zwConfig";
import { useEditorSessionStore } from "./useEditorSessionStore";
import { useProjectGoalsStore } from "./useProjectGoalsStore";
import {
  initialProjectSettingsModel,
  updateProjectSettings,
  type ProjectSettingsCmd,
  type ProjectSettingsMsg,
  type ProjectSettingsSavePayload,
} from "./useProjectSettingsStore.update";

export type { ProjectSettingsSavePayload };

/**
 * Project settings dialog bridge and `.zwconfig` writers.
 *
 * TEA: pure Model/Msg/Cmd in `useProjectSettingsStore.update.ts`. This shell
 * passes the current config in each Msg and interprets Cmds (dialog handlers,
 * optimistic write + rollback, goal baseline refresh).
 */
export const useProjectSettingsStore = defineStore("projectSettings", () => {
  const session = useEditorSessionStore();

  const model = shallowRef(initialProjectSettingsModel);

  const goals = computed(() =>
    normalizeZwGoals(
      isJust(session.projectConfig) ? session.projectConfig.value.goals : null,
    ),
  );

  /** Writes `next` optimistically, rolling back to the previous config if the
   * write fails. Every simple "apply this new config and persist it" call
   * goes through here (via the `writeConfig` Cmd). `onSuccess` runs only after
   * a successful write. */
  async function writeConfigOptimistic(
    next: ZwConfig,
    label: string,
    onSuccess?: () => Promise<void> | void,
  ): Promise<void> {
    if (isNothing(session.rootPath) || isNothing(session.projectConfig)) return;
    const previous = session.projectConfig;
    session.projectConfig = just(next);
    try {
      session.projectConfig = just(
        await writeZwConfig(session.rootPath.value, next),
      );
      await onSuccess?.();
    } catch (error) {
      console.error(label, error);
      session.projectConfig = previous;
    }
  }

  async function runCmd(cmd: ProjectSettingsCmd): Promise<void> {
    switch (cmd.kind) {
      case "openSettings":
        matchMaybe(cmd.section, {
          just: (section) => cmd.fn(section),
          nothing: () => cmd.fn(),
        });
        return;
      case "applyFonts":
        cmd.fn(cmd.fonts);
        return;
      case "writeConfig": {
        const { next, label, saved } = cmd;
        if (isNothing(saved)) {
          await writeConfigOptimistic(next, label);
          return;
        }
        const { notify, resetSession } = saved.value;
        await writeConfigOptimistic(next, label, async () => {
          if (isJust(notify)) notify.value();
          await useProjectGoalsStore().ensureGoalProgress(
            resetSession ? { resetSession: true } : {},
          );
        });
        return;
      }
      default:
        assertNever(cmd);
    }
  }

  function send(msg: ProjectSettingsMsg): Promise<void> {
    return dispatchInto(model, msg, updateProjectSettings, runCmd);
  }

  /** Writes a new manuscript order (acts and chapters) optimistically. Every
   * structural reorder goes through here: outline drag, Storyboard drag,
   * keyboard moves, and the exact-slot correction after a cross-chapter
   * scene move. */
  async function updateManuscript(manuscript: ManuscriptItem[]): Promise<void> {
    await send({
      type: "UpdateManuscript",
      config: session.projectConfig,
      manuscript,
    });
  }

  /** Swaps one chapter's scenes in place, leaving act nesting untouched. */
  async function updateChapterScenes(
    folder: string,
    scenes: ZwScene[],
  ): Promise<void> {
    await send({
      type: "UpdateChapterScenes",
      config: session.projectConfig,
      folder,
      scenes,
    });
  }

  async function updateSettings(
    payload: ProjectSettingsSavePayload,
  ): Promise<void> {
    await send({
      type: "UpdateSettings",
      config: session.projectConfig,
      payload,
    });
  }

  async function addDatabaseField(def: ZwDatabaseFieldDef): Promise<void> {
    await send({
      type: "AddDatabaseField",
      config: session.projectConfig,
      def,
    });
  }

  async function addDatabaseRelationPair(
    pair: ZwDatabaseRelationPair,
  ): Promise<void> {
    await send({
      type: "AddDatabaseRelationPair",
      config: session.projectConfig,
      pair,
    });
  }

  function openSettings(section?: ProjectSettingsSectionId): void {
    void send({ type: "OpenSettings", section: toMaybe(section) });
  }

  function registerOpenSettings(
    fn: (section?: ProjectSettingsSectionId) => void,
  ): void {
    void send({ type: "RegisterOpenSettings", fn });
  }

  function registerApplyFonts(fn: (fonts: ZwFonts) => void): void {
    void send({ type: "RegisterApplyFonts", fn });
  }

  function registerOnSettingsSaved(fn: () => void): void {
    void send({ type: "RegisterOnSettingsSaved", fn });
  }

  return {
    goals,
    openSettings,
    registerOpenSettings,
    registerApplyFonts,
    registerOnSettingsSaved,
    updateManuscript,
    updateChapterScenes,
    updateSettings,
    addDatabaseField,
    addDatabaseRelationPair,
    writeConfigOptimistic,
  };
});
